// src/components/seances/SeanceDetails.jsx
import React from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeftOutlined,
  SaveOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Empty,
  Form,
  Input,
  List,
  message,
  Modal,
  Popconfirm,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import { useTranslation } from "react-i18next";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import ReactPlayer from "react-player";
import Constants from "../Utils/Constants";
import BlocCatalog from "./BlocCatalog";

export default function SeanceDetails({ sessionKey: propKey, onBack }) {
  const { t, i18n } = useTranslation("seances", { useSuspense: false });
  const navigate = useNavigate();
  const { sessionKey: routeKey } = useParams();
  const sessionKey = propKey || routeKey;

  const [form] = Form.useForm();

  const token = localStorage.getItem("token");

  const authHeaders = {
    Authorization: "Bearer " + token,
    "Accept-Language": i18n.language,
  };

  const jsonHeaders = {
    ...authHeaders,
    "Content-Type": "application/json",
  };

  const [session, setSession] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [fetching, setFetching] = React.useState(false);

  const [ordered, setOrdered] = React.useState([]);
  const [isDragOver, setIsDragOver] = React.useState(false);

  const [sessionVideoKeys, setSessionVideoKeys] = React.useState([]);
  const [sessionVideoLoading, setSessionVideoLoading] = React.useState(false);
  const [videoSearch, setVideoSearch] = React.useState("");

  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [playbackUrl, setPlaybackUrl] = React.useState(null);

  const dragType = "application/x-bloc";

  const isMinioSessionKey = (value) =>
    typeof value === "string" && value.startsWith("sessions/");

  const openModal = (text, isError = false) => {
    Modal[isError ? "error" : "success"]({
      title: isError
        ? t("label_error") || "Erreur"
        : t("label_success") || "Succès",
      content: text,
    });
  };

  function extractBlocsFromSession(data) {
    const rows = data?.Bloc_Sessions || data?.Bloc_Session || [];

    return rows
      .map((bs) => {
        const bloc = bs?.Bloc || null;

        if (!bloc) return null;

        return {
          id: bloc.id ?? bs.BlocId ?? null,
          key: bloc.key ?? null,
          name: bloc.name ?? "Bloc",
        };
      })
      .filter(Boolean);
  }

  function reconcileOrdered(serverBlocs) {
    const server = Array.isArray(serverBlocs) ? serverBlocs : [];
    const getUid = (bloc) => bloc?.id ?? bloc?.key;
    const byUid = new Map(server.map((bloc) => [getUid(bloc), bloc]));

    const kept = ordered
      .filter((bloc) => byUid.has(getUid(bloc)))
      .map((bloc) => byUid.get(getUid(bloc)));

    const newOnes = server.filter(
      (bloc) => !ordered.some((oldBloc) => getUid(oldBloc) === getUid(bloc))
    );

    return [...kept, ...newOnes];
  }

  const fetchSessionVideos = async () => {
    setSessionVideoLoading(true);

    try {
      const { data } = await axios.get(
        `${Constants.SERVER_URL}/api/minio/session-videos`,
        { headers: authHeaders }
      );

      setSessionVideoKeys(data?.keys || []);
    } catch {
      setSessionVideoKeys([]);
    } finally {
      setSessionVideoLoading(false);
    }
  };

  const refreshPlaybackUrl = async (videoUrlValue) => {
    const cleanValue = String(videoUrlValue || "").trim();

    if (!cleanValue) {
      setPlaybackUrl(null);
      return;
    }

    if (!isMinioSessionKey(cleanValue)) {
      setPlaybackUrl(cleanValue);
      return;
    }

    try {
      const { data } = await axios.get(
        `${Constants.SERVER_URL}/api/minio/presign-download`,
        {
          headers: authHeaders,
          params: {
            bucket: "rxapa-videos",
            key: cleanValue,
          },
        }
      );

      setPlaybackUrl(data?.downloadUrl || null);
    } catch {
      setPlaybackUrl(null);
    }
  };

  const fetchSession = async () => {
    if (!sessionKey) return;

    setFetching(true);

    try {
      const { data } = await axios.get(
        `${Constants.SERVER_URL}/session/${encodeURIComponent(sessionKey)}`,
        { headers: authHeaders }
      );

      setSession(data);

      const cleanVideoUrl = String(data?.videoUrl || "").trim();

      form.setFieldsValue({
        name: data?.name || "",
        description: data?.description || "",
        videoUrl: cleanVideoUrl,
      });

      await refreshPlaybackUrl(cleanVideoUrl);

      const serverBlocs = extractBlocsFromSession(data);
      setOrdered(reconcileOrdered(serverBlocs));
    } catch (err) {
      const raw =
        err?.response?.data?.message || err?.message || "unexpected_error";

      openModal(t(`Backend:${raw}`) || raw, true);
    } finally {
      setFetching(false);
    }
  };

  React.useEffect(() => {
    if (!sessionKey) {
      Modal.error({
        title: t("label_error") || "Erreur",
        content: "sessionKey manquant.",
        onOk: () => (onBack ? onBack() : navigate("/seances")),
      });

      setLoading(false);
      return;
    }

    async function init() {
      setLoading(true);
      await fetchSession();
      await fetchSessionVideos();
      setLoading(false);
    }

    init();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionKey]);

  const handleVideoChangeFromPicker = async (key) => {
    const cleanKey = String(key || "").trim();
    form.setFieldsValue({ videoUrl: cleanKey });
    await refreshPlaybackUrl(cleanKey);
  };

  const onSaveSessionInfo = async () => {
    const values = form.getFieldsValue(true);

    const body = {
      name: String(values.name || "").trim(),
      description: String(values.description || "").trim(),
      videoUrl: String(values.videoUrl || "").trim(),
    };

    if (!body.name) {
      message.warning(
        t("label_session_name_required") || "Le nom de la séance est requis"
      );
      return;
    }

    if (!body.videoUrl || !body.videoUrl.startsWith("sessions/")) {
      message.warning("Veuillez choisir une vidéo MinIO.");
      return;
    }

    try {
      const res = await axios.put(
        `${Constants.SERVER_URL}/update-session/${encodeURIComponent(
          sessionKey
        )}`,
        body,
        { headers: jsonHeaders }
      );

      const raw = res?.data?.message || "session_updated";

      Modal.destroyAll();

      message.success(t(`Backend:${raw}`) || "Séance mise à jour");

      if (onBack) {
        onBack();
      } else {
        navigate("/seances", { replace: true });
      }
    } catch (err) {
      const raw =
        err?.response?.data?.message || err?.message || "unexpected_error";

      openModal(t(`Backend:${raw}`) || "Erreur lors de la mise à jour", true);
    }
  };

  const addBlocToSession = (input, opts = {}) => {
    const blocId = input?.id ?? input?.blocId ?? null;
    const blocKey = input?.key ?? input?.blocKey ?? null;
    const blocName = input?.name ?? input?.blocName ?? "Bloc";

    const body = {
      blocId,
      blocKey,
      required: opts.required ?? true,
      dayTime: opts.dayTime ?? null,
    };

    if (!blocId && !blocKey) {
      openModal("Identifiant du bloc manquant.", true);
      return;
    }

    axios
      .post(
        `${Constants.SERVER_URL}/${encodeURIComponent(sessionKey)}/add-bloc`,
        body,
        { headers: jsonHeaders }
      )
      .then((res) => {
        const raw = res?.data?.message || "bloc_added_to_session";

        message.success(t(`Backend:${raw}`) || "Bloc ajouté");

        const uid = blocId ?? blocKey;

        setOrdered((prev) =>
          prev.some((bloc) => (bloc.id ?? bloc.key) === uid)
            ? prev
            : [...prev, { id: blocId, key: blocKey, name: blocName }]
        );

        fetchSession();
      })
      .catch((err) => {
        const raw =
          err?.response?.data?.message || err?.message || "unexpected_error";

        openModal(t(`Backend:${raw}`) || "Impossible d’ajouter le bloc", true);
      });
  };

  const removeBloc = (blocKeyOrId) => {
    if (!session?.id) {
      message.error(t("Backend:session_not_found") || "Séance introuvable");
      return;
    }

    setOrdered((prev) =>
      prev.filter((bloc) => (bloc.id ?? bloc.key) !== blocKeyOrId)
    );

    axios
      .delete(`${Constants.SERVER_URL}/remove-bloc-from-session`, {
        headers: jsonHeaders,
        data: {
          sessionId: session.id,
          blocId: blocKeyOrId,
        },
      })
      .then((res) => {
        const raw = res?.data?.message || "bloc_removed_from_session";

        message.success(t(`Backend:${raw}`) || "Bloc retiré");
        fetchSession();
      })
      .catch((err) => {
        const raw =
          err?.response?.data?.message || err?.message || "unexpected_error";

        openModal(t(`Backend:${raw}`) || "Suppression impossible", true);
        fetchSession();
      });
  };

  const handleDragOver = (event) => {
    const ok = event.dataTransfer?.types?.includes(dragType);

    if (!ok) return;

    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";

    if (!isDragOver) setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const handleDrop = (event) => {
    const raw = event.dataTransfer.getData(dragType);

    setIsDragOver(false);

    if (!raw) return;

    event.preventDefault();

    let payload = null;

    try {
      payload = JSON.parse(raw);
    } catch {
      payload = null;
    }

    if (!payload) return;

    addBlocToSession(payload, {
      required: true,
      dayTime: null,
    });
  };

  if (loading) {
    return (
      <div style={{ padding: 24, display: "flex", justifyContent: "center" }}>
        <Spin tip={t("label_loading_session") || "Chargement de la séance…"} />
      </div>
    );
  }

  if (!session) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          t("Backend:session_not_found") || "Séance introuvable ou erreur"
        }
      />
    );
  }

  const filteredSessionVideos = sessionVideoKeys.filter((key) =>
    String(key).toLowerCase().includes(videoSearch.trim().toLowerCase())
  );

  const currentVideoUrl = form.getFieldValue("videoUrl");

  return (
    <div className="session-details" style={{ padding: 24 }}>
      <Space align="center" style={{ marginBottom: 16 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={onBack || (() => navigate("/seances", { replace: true }))}
        >
          {t("label_back") || "Retour"}
        </Button>

        <Typography.Title level={3} style={{ margin: 0 }}>
          {t("label_edit_session") || "Édition de la séance"}
        </Typography.Title>

        {fetching && <Tag color="blue">MAJ…</Tag>}
      </Space>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card
            title={t("label_session_info") || "Informations de la séance"}
            extra={
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={onSaveSessionInfo}
              >
                {t("label_save") || "Enregistrer"}
              </Button>
            }
          >
            <Form form={form} layout="vertical">
              <Form.Item
                label={t("label_session_name") || "Nom de la séance"}
                name="name"
                rules={[
                  {
                    required: true,
                    message:
                      t("label_session_name_required") ||
                      "Le nom de la séance est requis",
                  },
                ]}
              >
                <Input maxLength={80} />
              </Form.Item>

              <Form.Item
                label={t("label_session_description") || "Description"}
                name="description"
              >
                <Input.TextArea
                  autoSize={{ minRows: 2, maxRows: 6 }}
                  maxLength={500}
                />
              </Form.Item>

              <Form.Item name="videoUrl" hidden>
                <Input />
              </Form.Item>

              <Form.Item label={t("label_session_video") || "Vidéo"}>
                <Input
                  placeholder="Rechercher une vidéo"
                  value={videoSearch}
                  onChange={(e) => setVideoSearch(e.target.value)}
                  allowClear
                />

                <div style={{ marginTop: 8 }}>
                  {sessionVideoLoading ? (
                    <Spin />
                  ) : (
                    <List
                      size="small"
                      bordered
                      style={{ maxHeight: 220, overflow: "auto" }}
                      dataSource={filteredSessionVideos}
                      locale={{ emptyText: "Aucune vidéo trouvée dans MinIO." }}
                      renderItem={(key) => {
                        const selected = key === currentVideoUrl;

                        return (
                          <List.Item
                            onClick={() => handleVideoChangeFromPicker(key)}
                            style={{
                              cursor: "pointer",
                              background: selected
                                ? "rgba(22,119,255,0.08)"
                                : "transparent",
                            }}
                            actions={[
                              <Button
                                key="select"
                                type={selected ? "primary" : "default"}
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleVideoChangeFromPicker(key);
                                }}
                              >
                                {selected ? "Sélectionnée" : "Sélectionner"}
                              </Button>,
                            ]}
                          >
                            <Typography.Text ellipsis style={{ maxWidth: 520 }}>
                              {key}
                            </Typography.Text>
                          </List.Item>
                        );
                      }}
                    />
                  )}
                </div>

                <div style={{ marginTop: 10 }}>
                  <Typography.Text type="secondary">
                    Valeur enregistrée :{" "}
                    <Typography.Text code>
                      {currentVideoUrl || "—"}
                    </Typography.Text>
                  </Typography.Text>
                </div>

                <div style={{ marginTop: 10 }}>
                  <Button
                    icon={<PlayCircleOutlined />}
                    disabled={!playbackUrl}
                    onClick={() => setPreviewOpen(true)}
                  >
                    Aperçu vidéo
                  </Button>
                </div>
              </Form.Item>
            </Form>
          </Card>

          <Card
            style={{ marginTop: 16 }}
            title={
              <Space>
                {t("label_session_blocs") || "Blocs de la séance"}
                <Tag>{ordered.length}</Tag>
              </Space>
            }
            extra={
              <Space>
                <ExclamationCircleOutlined style={{ color: "#faad14" }} />
                <Typography.Text type="secondary">
                  {t("hint_drag_from_right") ||
                    "Glisse des blocs depuis la colonne de droite"}
                </Typography.Text>
              </Space>
            }
          >
            <DragDropContext
              onDragEnd={(result) => {
                if (!result.destination) return;

                const next = Array.from(ordered);
                const [moved] = next.splice(result.source.index, 1);

                next.splice(result.destination.index, 0, moved);
                setOrdered(next);
              }}
            >
              <Droppable droppableId="sessionBlocs" direction="horizontal">
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    style={{
                      padding: 8,
                      border: `2px dashed ${
                        isDragOver || ordered.length === 0
                          ? "#1677ff"
                          : "transparent"
                      }`,
                      borderRadius: 12,
                      minHeight: 80,
                      display: "flex",
                      gap: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    {ordered.length === 0 ? (
                      <Empty
                        description={
                          t("label_no_blocs") || "Aucun bloc pour l’instant"
                        }
                      />
                    ) : (
                      ordered.map((bloc, index) => {
                        const uid = bloc?.id ?? bloc?.key;

                        if (!uid) return null;

                        return (
                          <Draggable
                            key={String(uid)}
                            draggableId={String(uid)}
                            index={index}
                          >
                            {(dragProvided) => (
                              <div
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                              >
                                <Card
                                  hoverable
                                  actions={[
                                    <Popconfirm
                                      key="remove"
                                      title={
                                        t("label_confirm_remove_bloc") ||
                                        "Retirer ce bloc ?"
                                      }
                                      okText={t("label_remove") || "Retirer"}
                                      cancelText={
                                        t("label_cancel") || "Annuler"
                                      }
                                      okButtonProps={{ danger: true }}
                                      onConfirm={() =>
                                        removeBloc(bloc.id ?? bloc.key)
                                      }
                                    >
                                      <button
                                        type="button"
                                        style={{
                                          border: "none",
                                          background: "transparent",
                                          cursor: "pointer",
                                        }}
                                      >
                                        <DeleteOutlined
                                          style={{ color: "#ff4d4f" }}
                                        />
                                      </button>
                                    </Popconfirm>,
                                  ]}
                                >
                                  <Typography.Text strong ellipsis>
                                    {bloc.name}
                                  </Typography.Text>
                                </Card>
                              </div>
                            )}
                          </Draggable>
                        );
                      })
                    )}

                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title={t("label_bloc_catalog") || "Catalogue de blocs"}>
            <BlocCatalog
              onOpenDetails={() => {}}
              onAddQuick={(bloc) =>
                addBlocToSession(bloc, {
                  required: true,
                  dayTime: null,
                })
              }
            />
          </Card>
        </Col>
      </Row>

      <Modal
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={null}
        title="Aperçu vidéo"
        width={900}
        destroyOnClose
      >
        {playbackUrl ? (
          <div style={{ aspectRatio: "16/9" }}>
            <ReactPlayer
              url={playbackUrl}
              controls
              width="100%"
              height="100%"
            />
          </div>
        ) : (
          <Empty description="URL signée non générée." />
        )}
      </Modal>
    </div>
  );
}